/**
 * Cart Module - Xử lý việc lấy dữ liệu giỏ hàng và cập nhật UI
 * Gọi API /cart/indexajax để lấy thông tin giỏ hàng hiện tại
 */
class Cart {
  constructor() {
    this.cartTrigger = document.querySelector("#cart-trigger");
    this.cartBadges = document.querySelectorAll(".cart-quantity-badge");
    this.apiEndpoint = "/cart/indexajax";
    this.toastContainer = null;

    // Chỉ khởi tạo nếu tìm thấy cart trigger hoặc cart badges
    if (this.cartTrigger || this.cartBadges.length > 0) {
      this.init();
    }
  }

  init() {
    this.loadCartData();
    this.createToastContainer();

    // Lắng nghe sự kiện cart:updated từ các module khác
    document.addEventListener("cart:updated", (event) => {
      if (event.detail && event.detail.cart) {
        // Sử dụng dữ liệu từ event thay vì gọi lại API
        this.updateCartBadgeFromCartData(event.detail.cart);

        // Hiển thị toast nếu có thông tin sản phẩm được thêm
        if (event.detail.productAdded) {
          this.showSuccessToast(event.detail.productAdded);
        }
      } else {
        // Fallback: gọi lại API nếu không có dữ liệu
        this.loadCartData();
      }
    });

    // Lắng nghe sự kiện cart:product-added
    document.addEventListener("cart:product-added", (event) => {
      if (event.detail && event.detail.product) {
        this.showSuccessToast(event.detail.product);
      }
    });
  }

  /**
   * Tạo container cho toast notifications
   */
  createToastContainer() {
    // Kiểm tra xem container đã tồn tại chưa
    if (document.querySelector(".cart-toast-container")) {
      return;
    }

    this.toastContainer = document.createElement("div");
    this.toastContainer.className =
      "cart-toast-container fixed bottom-4 right-4 z-50 space-y-2";
    document.body.appendChild(this.toastContainer);
  }

  /**
   * Hiển thị toast thông báo thêm sản phẩm thành công
   * @param {Object} product - Thông tin sản phẩm được thêm
   */
  showSuccessToast(product) {
    const toast = document.createElement("div");
    toast.className =
      "cart-toast bg-white border border-gray-200 rounded-lg shadow-lg p-4 max-w-sm transform transition-all duration-300 translate-x-full";

    let countdown = 5;
    const productName = product.name || product.product_name || "Sản phẩm";
    const cartUrl = window.storefrontLangUrl ? window.storefrontLangUrl("/cart") : "/cart";
    const checkoutUrl = window.storefrontLangUrl ? window.storefrontLangUrl("/checkout") : "/checkout";

    toast.innerHTML = `
      <div class="flex items-start justify-between">
        <div class="flex items-center space-x-3">
          <div class="flex-shrink-0">
            <svg class="w-6 h-6 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
            </svg>
          </div>
          <div class="flex-1">
            <h4 class="text-sm font-medium text-gray-900">Đã thêm vào giỏ hàng</h4>
            <p class="text-sm text-gray-600 mt-1">${productName}</p>
            <div class="flex space-x-2 mt-3">
              <a href="${cartUrl}" class="text-xs bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-600 transition-colors">
                Xem giỏ hàng
              </a>
              <a href="${checkoutUrl}" class="text-xs bg-green-500 text-white px-3 py-1 rounded hover:bg-green-600 transition-colors">
                Thanh toán
              </a>
            </div>
          </div>
        </div>
        <button class="toast-close text-gray-400 hover:text-gray-600 transition-colors">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
      </div>
      <div class="mt-3">
        <div class="bg-gray-200 rounded-full h-1">
          <div class="toast-progress bg-green-500 h-1 rounded-full transition-all duration-1000" style="width: 100%"></div>
        </div>
        <p class="text-xs text-gray-500 mt-1">Tự động đóng sau <span class="toast-countdown">${countdown}</span>s</p>
      </div>
    `;

    // Thêm toast vào container
    this.toastContainer.appendChild(toast);

    // Hiển thị toast với animation
    setTimeout(() => {
      toast.classList.remove("translate-x-full");
    }, 100);

    // Xử lý nút đóng
    const closeBtn = toast.querySelector(".toast-close");
    closeBtn.addEventListener("click", () => {
      this.hideToast(toast);
    });

    // Đếm ngược và tự động đóng
    const countdownElement = toast.querySelector(".toast-countdown");
    const progressBar = toast.querySelector(".toast-progress");

    const countdownInterval = setInterval(() => {
      countdown--;
      countdownElement.textContent = countdown;

      // Cập nhật progress bar
      const progressPercent = (countdown / 5) * 100;
      progressBar.style.width = `${progressPercent}%`;

      if (countdown <= 0) {
        clearInterval(countdownInterval);
        this.hideToast(toast);
      }
    }, 1000);
  }

  /**
   * Ẩn toast với animation
   * @param {HTMLElement} toast - Element toast cần ẩn
   */
  hideToast(toast) {
    toast.classList.add("translate-x-full");
    setTimeout(() => {
      if (toast.parentNode) {
        toast.parentNode.removeChild(toast);
      }
    }, 300);
  }

  /**
   * Gọi API để lấy dữ liệu giỏ hàng hiện tại
   */
  async loadCartData() {
    try {
      const response = await fetch(this.apiEndpoint, {
        method: "GET",
        headers: {
          "X-Requested-With": "XMLHttpRequest",
          Accept: "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        const cartPayload =
          data?.data?.cart && typeof data.data.cart === "object"
            ? data.data.cart
            : data?.cart && typeof data.cart === "object"
              ? data.cart
              : { quantity: 0, details: [] };
        this.updateCartBadge(cartPayload);
      } else {
        console.warn("⚠️ Cart API returned success: false");
        this.updateCartBadge({ quantity: 0, details: [] });
      }
    } catch (error) {
      console.error("❌ Error loading cart data:", error);
      // Fallback: ẩn badge nếu có lỗi
      this.updateCartBadge({ quantity: 0, details: [] });
    }
  }

  /**
   * Cập nhật badge số lượng giỏ hàng từ dữ liệu cart object
   * @param {Object} cart - Dữ liệu giỏ hàng từ API
   */
  updateCartBadgeFromCartData(cart) {
    if (this.cartBadges.length === 0) {
      console.warn("⚠️ Cart badge elements not found");
      return;
    }

    let totalQuantity = 0;

    // Constants for weight sale mode (matching Product.ts)
    const WEIGHT_SALE_MODE_WEIGHT = 3;
    const WEIGHT_SALE_MODE_COUNTING = 5;

    // Tính tổng số lượng từ cart.details
    if (cart.details && Array.isArray(cart.details)) {
      totalQuantity = cart.details.reduce((sum, item) => {
        const uomQuantity = item.uom_quantity || item.uomquantity || 0;

        // Check if item is weight product
        const isWeightProduct =
          item.weight_sale_mode === WEIGHT_SALE_MODE_WEIGHT ||
          item.weight_sale_mode === WEIGHT_SALE_MODE_COUNTING;

        if (isWeightProduct) {
          if (item.weight_preset_factor > 0) {
            // Has preset: divide by factor
            return sum + uomQuantity / item.weight_preset_factor;
          }
          // No preset: convert grams to kg
          return sum + uomQuantity / 1000;
        }

        return sum + uomQuantity;
      }, 0);
    }

    // Round total quantity to handle decimal from division
    totalQuantity = Math.round(totalQuantity);

    // Cập nhật tất cả cart badges
    this.cartBadges.forEach((badge) => {
      badge.textContent = totalQuantity;
      if (totalQuantity > 0) {
        badge.style.display = "inline";
        badge.classList.remove("bg-gray-100", "text-gray-300");
        // Thêm lại class màu nền/text mặc định nếu cần
        badge.classList.add("bg-red-500", "text-white");
      } else {
        // Ẩn hoàn toàn badge khi totalQuantity = 0 (trừ khi có class keep-visible)
        if (badge.classList.contains("keep-visible")) {
          badge.style.display = "inline";
        } else {
          badge.style.display = "none";
        }
      }
    });
  }

  /**
   * Cập nhật badge số lượng giỏ hàng
   * @param {Object} cart - Dữ liệu giỏ hàng từ API
   */
  updateCartBadge(cart) {
    this.updateCartBadgeFromCartData(cart);
  }

  /**
   * Refresh cart data (có thể gọi từ bên ngoài)
   */
  refresh() {
    this.loadCartData();
  }
}

// Export để có thể sử dụng từ bên ngoài
export default Cart;
